import React, { useEffect, useRef, useState } from "react";

import { DURACAO_MINIMA, duracaoDaCaminhada } from "./caminhada";

/**
 * O explorador sobre a trilha, e o movimento dele de um nó ao próximo.
 *
 * Era um `<img>` dentro do nó atual, o que tornava o avanço um TELEPORTE: no
 * avanço de fase o avatar desmontava de um botão e montava em outro, sem estado
 * intermediário para animar. Aqui ele é uma camada própria do tabuleiro,
 * posicionada nas mesmas coordenadas que os nós e o caminho SVG usam.
 *
 * O deslocamento anima `left`/`top`, e não `transform` nem `offset-path`:
 *
 * - Interpolar as duas linearmente descreve uma RETA no espaço da tela, que é
 *   exatamente o segmento que `construirPath` desenha entre dois nós (ele usa
 *   `L`, não curva). O avatar anda sobre a linha tracejada de graça.
 * - `offset-path` seria o caminho "certo" e tem suporte irregular no navegador
 *   de escola que o AVA atende — o mesmo motivo pelo qual o rótulo do nó usa
 *   `-webkit-line-clamp`.
 * - `transform` exigiria MEDIR a faixa, e medir na montagem é uma armadilha
 *   conhecida deste módulo: a trilha nasce dentro de um `tab-pane` sem área. Com
 *   os dois eixos em porcentagem, não há o que medir.
 *
 * O custo é animar propriedades de layout. É aceitável aqui porque o elemento é
 * `position: absolute` e nada na faixa depende da posição dele.
 *
 * QUEM DECIDE QUE HÁ CAMINHADA É A FASE, não a coordenada. Parece a mesma coisa e
 * não é: as coordenadas são porcentagem da faixa, e a faixa muda de largura por
 * motivos que não são avanço nenhum — a primeira medição da pista, um resize da
 * janela, e sobretudo a leva nova revelada ao concluir uma fase premiada, que
 * reparte a faixa entre mais nós e reescreve TODAS as porcentagens de uma vez.
 *
 * Enquanto o gatilho era a coordenada, cada uma dessas disparava uma caminhada, e
 * caminhada nascida de reposicionamento tem distância pequena: caía na duração
 * mínima e lia como salto. Foi relatado como "ao receber as cartas, o avatar pula
 * muito rápido" — as cartas são justamente o que precede a troca de leva.
 *
 * E A CAMINHADA SÓ CONTA SE O ALUNO PUDER VÊ-LA. `congelado` não basta para
 * garantir isso, porque ele chega um commit TARDE: quem cobre a tela é a cerimônia
 * do card, aberta por um efeito de `TrilhaDeAtividade` — um ANCESTRAL — reagindo ao
 * mesmo modelo novo que traz a fase concluída. React roda efeitos de baixo para
 * cima, então o efeito daqui roda ANTES do que abre a carta, e existe um commit
 * inteiro em que a fase já avançou e `congelado` ainda é `false`. O avanço era
 * consumido nesse commit: o avatar caminhava atrás do scrim, o cronômetro corria
 * escondido, e ao fechar a carta não havia mais nada para animar — o explorador
 * aparecia na fase seguinte. Medido em 02/08 com
 * `ferramentas-diagnostico/tc-caminhada-visivel.js`, cenário 2.
 *
 * Por isso a caminhada é DESFEITA quando o host cobre a tela no meio dela: o
 * avatar volta ao nó de partida e o avanço fica pendente, para acontecer à vista
 * quando a carta sair. Não é "esperar o momento certo" — é não dar por acontecido
 * o que ninguém viu.
 *
 * @param {string}  avatar          URL da figura
 * @param {number}  fase            número da fase em que o explorador está
 * @param {number}  esquerdaPct     distância percorrida até a fase atual, em % da faixa
 * @param {number}  topoPct         altura do CENTRO da fase atual, em % da faixa
 * @param {number}  [espaco]        px entre dois nós — a régua do percurso
 * @param {number}  [deslocamentoY] px acima do centro do nó (negativo sobe)
 * @param {boolean} [congelado]     host tem algo por cima: não sai do lugar ainda
 * @param {boolean} [semAnimacao]   pular direto para a posição nova, sem caminhar
 */
function AvatarCaminhante({
  avatar,
  fase,
  esquerdaPct,
  topoPct,
  espaco = 184,
  deslocamentoY = 0,
  congelado = false,
  semAnimacao = false
}) {
  // A posição DESENHADA, que não é a de entrada enquanto `congelado`. É o que
  // faz o avatar esperar a cerimônia do card sair antes de andar: sem isso ele
  // caminharia atrás do scrim da carta, e em 6 das 10 fases de uma trilha de 10
  // (as premiadas) ninguém veria a animação.
  //
  // Carrega a FASE junto das coordenadas, e não num ref à parte: é a fase que a
  // posição desenhada representa, e as duas têm de voltar juntas quando uma
  // caminhada é desfeita — separadas, o avatar voltava ao nó de partida com a
  // fase já contada como vencida e nunca mais caminhava.
  const [posicao, setPosicao] = useState({ esquerdaPct, topoPct, fase });
  // `null` = parado. Caminhando, guarda a duração, um número de sequência — que é
  // o que faz uma caminhada nova reiniciar o cronômetro da anterior em vez de
  // herdar o fim dela — e de onde ela partiu, para poder ser desfeita.
  const [trajeto, setTrajeto] = useState(null);
  const sequencia = useRef(0);

  // Desfaz a caminhada que NASCEU coberta — e só ela.
  //
  // A distinção é entre duas coisas que `congelado` sozinho não separa: uma
  // caminhada que o host cobriu no mesmo commit em que ela começou (ninguém a viu,
  // e desfazer é o conserto) e uma que já estava na tela quando a carta abriu (o
  // aluno viu, e desfazer joga o avatar para trás). O segundo caso é real e
  // frequente: concluir duas fases em menos de um trajeto, a segunda premiada — foi
  // o que o teste de integração da passagem de leva pegou, com o explorador
  // recuando dois nós.
  //
  // `visto` é a marca de "este trajeto chegou a ser pintado": ele é ligado no
  // primeiro render em que a caminhada existe e a tela está livre.
  //
  // Ajuste durante o RENDER, e não num efeito: o congelamento chega no mesmo
  // commit em que a caminhada começa (ver o cabeçalho), e um efeito só rodaria
  // depois do paint — o frame que traz o scrim já teria o avatar em movimento, e
  // desfazer dali seria um vai-e-volta à vista. Escrever estado no próprio render é
  // o padrão do React para ajustar estado quando a prop muda: ele re-renderiza
  // antes de pintar, então o estado intermediário não chega à tela.
  //
  // Só state, nenhum ref: em StrictMode o render roda duas vezes, e mutação de ref
  // aqui deixaria a segunda passada sem a posição de partida.
  if (trajeto && !trajeto.visto) {
    if (congelado) {
      setTrajeto(null);
      setPosicao(trajeto.partida);
    } else {
      setTrajeto({ ...trajeto, visto: true });
    }
  }

  useEffect(() => {
    if (congelado) return;
    if (posicao.esquerdaPct === esquerdaPct && posicao.topoPct === topoPct) {
      // Mesma coordenada, fase outra: acontece quando a faixa se reparte de um
      // jeito que devolve o nó novo ao pixel do antigo. Não há percurso a andar,
      // mas a fase desenhada tem de acompanhar — senão o próximo reposicionamento
      // acharia que houve avanço e sairia deslizando.
      if (posicao.fase !== fase) {
        setPosicao({ esquerdaPct, topoPct, fase });
      }
      return;
    }

    const fasesVencidas = Math.abs(fase - posicao.fase);
    setPosicao({ esquerdaPct, topoPct, fase });

    // Passagem de leva: o host está trocando a faixa inteira atrás de uma cortina,
    // e o que houver de movimento aqui acontece escondido.
    if (semAnimacao) {
      setTrajeto(null);
      return;
    }

    if (!fasesVencidas) {
      // Reposicionamento sem avanço: a faixa mudou de tamanho debaixo do avatar.
      //
      // PARADO, ele só precisa estar no lugar certo — animar aqui é o defeito
      // antigo ("ao receber as cartas o avatar pula muito rápido").
      //
      // CAMINHANDO, o que mudou foi o DESTINO, não o rumo: o trajeto continua e a
      // transição reinterpola do ponto em que o avatar está até a posição nova.
      // Zerar o trajeto aqui matava a caminhada em curso, e no AVA isso acontecia
      // justamente no instante em que ela começa — fechar a cerimônia desmonta um
      // portal e o modal do item devolve ao body a barra de rolagem e o
      // `padding-right` do Bootstrap; a pista muda de largura, o ResizeObserver
      // dispara e as porcentagens são reescritas.
      //
      // Medido em 03/08 com `harness-trilha/medir-interrupcao.js`: 9px de pista aos
      // 250ms bastavam para o trilho virar duas posições e a classe `--andando`
      // cair em 150ms. Aparecia só onde os nós estão ESPALHADOS (percurso natural
      // menor que a pista), porque é só ali que a largura da pista entra na conta
      // das porcentagens — foi por isso que numa trilha de 11 fases a leva 1
      // saltava e a leva 2, com o espaçamento no mínimo, andava.
      //
      // O cronômetro reinicia junto (sequência nova) porque a transição CSS também
      // reinicia: ela vai gastar a duração cheia para chegar ao destino novo, e um
      // relógio ainda contando o trajeto antigo cortaria o fim da caminhada.
      if (trajeto) {
        sequencia.current += 1;
        setTrajeto({ ...trajeto, seq: sequencia.current });
      }
      return;
    }

    // A duração vem do percurso, e o cálculo mora em `./caminhada` porque a câmera
    // também precisa dele: ela espera a caminhada terminar para reenquadrar.
    const duracao = duracaoDaCaminhada(fasesVencidas, espaco);

    sequencia.current += 1;
    // `partida` é a posição de onde esta caminhada saiu, fase inclusive. É o que
    // permite devolver o avatar ao nó anterior se o host cobrir a tela antes de a
    // caminhada aparecer.
    setTrajeto({ duracao, seq: sequencia.current, partida: posicao });
    // `trajeto` é dependência porque o efeito agora PERGUNTA se há caminhada em
    // curso antes de tratar um reposicionamento. Não gera laço: toda re-execução
    // provocada por ele cai no primeiro `return` — as coordenadas de entrada já são
    // iguais às desenhadas quando o trajeto muda.
  }, [
    fase,
    esquerdaPct,
    topoPct,
    espaco,
    congelado,
    semAnimacao,
    posicao,
    trajeto
  ]);

  // O cronômetro mora num efeito PRÓPRIO, e não junto do que dispara a
  // caminhada. Naquele, `posicao` é dependência (ele a compara) e também o que
  // ele escreve: a re-execução seguinte rodava o cleanup e cancelava o timer que
  // ela mesma tinha acabado de criar, então o avatar nunca parava de andar.
  //
  // As dependências são a SEQUÊNCIA e a duração, não o objeto: marcar o trajeto
  // como visto cria um objeto novo para a MESMA caminhada, e com o objeto na
  // dependência isso reiniciava o relógio dela.
  const seq = trajeto && trajeto.seq;
  const duracaoDoTrajeto = trajeto && trajeto.duracao;
  useEffect(() => {
    if (!seq) return undefined;
    const id = setTimeout(() => setTrajeto(null), duracaoDoTrajeto);
    return () => clearTimeout(id);
  }, [seq, duracaoDoTrajeto]);

  const andando = trajeto !== null;
  const duracaoMs = andando ? trajeto.duracao : DURACAO_MINIMA;

  return (
    <div
      className={`trilha-ludica__caminhante${
        andando ? " trilha-ludica__caminhante--andando" : ""
      }`}
      style={{
        left: `${posicao.esquerdaPct}%`,
        top: `${posicao.topoPct}%`,
        // Sobe para cima do nó SEM entrar na transição: `top` é a porcentagem que
        // anima, e a margem é o offset constante somado a ela.
        marginTop: `${deslocamentoY}px`,
        // Zero enquanto parado: senão um reposicionamento sem caminhada (a
        // passagem de leva, uma correção de progresso vinda do servidor) sairia
        // deslizando pela tela em vez de acontecer e pronto.
        //
        // E zero aqui BASTA, mesmo que a posição mude no mesmo commit: quem decide
        // se a transição roda é a duração do estilo DEPOIS da mudança, não a de
        // antes (é o que faz `:hover { transition }` funcionar). Medido em 02/08 —
        // `ferramentas-diagnostico/tc-transicao-mesmo-tick.js`. Havia a hipótese
        // contrária, de que mudar duração e posição juntos teleportaria; ela é
        // falsa, e o teleporte relatado tinha outra causa (ver o cabeçalho).
        transitionDuration: andando ? `${Math.round(duracaoMs)}ms` : "0ms"
      }}
      data-testid="trilha-avatar"
    >
      <img className="trilha-ludica__avatar" src={avatar} alt="" />
    </div>
  );
}

export default AvatarCaminhante;
